import { useCallback, useRef, useState } from "react";
import { useFuncionariosListaFilters } from "../hooks/Funcionarios_lista/filters";
import { useNavigate } from "react-router";
import { processError } from "../../../../common/utils/base_error";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { FuncionarioTabela } from "../../domain/models/funcionario_tabela";
import { ReportsFuncionariosForm } from "../schemas/Funcionarios_lista/reports_funcionarios";
import { ReportsDocumentosForm } from "../schemas/Funcionarios_lista/reports_documentos";
import { ReportsTreinamentosForm } from "../schemas/Funcionarios_lista/reports_treinamentos";
import { useUserAuthStore } from "../../../../stores/global/userAuth";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useFuncionariosListaPagination } from "../hooks/Funcionarios_lista/pagination"; //
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useCachedDataLoader } from "../../../../common/hooks/use_cached_data_loader";
import { GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { AlterarCentroCustoForm } from "../schemas/Funcionarios_lista/alterar_centro_custo";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { RangedDate } from "../../../../common/utils/ranged_date_utils";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { TelasIds } from "../../../../common/utils/tela_ids";
import { FuncionarioCopyForm } from "../schemas/Funcionarios_lista/copy_funcionario";

import CommonConstants from "../../../../common/constants";

/**
 * useFuncionariosListaViewModel
 */
type LoadFuncionarioState = {
  tipoPesquisa?: number;
  pesquisa?: string;
  setor?: string;
  filial?: string;
  admissaoDe?: string;
  admissaoAte?: string;
  demissaoDe?: string;
  demissaoAte?: string;
  afastamentoDe?: string;
  afastamentoAte?: string;
  feriasDe?: string;
  feriasAte?: string;
  centroDeCusto?: number[];
  status?: number;
  treinamentos?: number;
  prazoExperiencia?: number;
  documentos?: number;
  sortField?: string;
  sort?: "asc" | "desc";
  page?: number;
  pageSize?: number;
};

export const useFuncionariosListaViewModel = () => {
  const {
    commons: {
      getPageSizeUseCase,
    },
    reports: {
      getReportCadastroFuncionarioUseCase,
      getReportFuncionariosUseCase,
      getReportTreinamentosUseCase,
      getReportDocumentosUseCase,
    },
    rh: {
      getFuncionariosUseCase,
      deleteFuncionarioUseCase,
      getSetoresDropDownUseCase,
      getFuncionarioNomesUseCase,
      getFuncionarioFiliaisUseCase,
      getTreinamentosDropDownUseCase,
      alterarCentroCustoFuncionariosUseCase,
      copyFuncionarioUseCase,
    },
  } = useDependencyInjection();

  const { loadCentroCustoDropDown, loadCargosDropDown } = useDropDownLoader();

  const debouncedRef = useCreateDebounceHandler(750);

  const menuRef = useRef<HTMLButtonElement>(null);

  const { idUsuario } = useUserAuthStore();

  const { pushErrorNotification, pushWarnNotification, pushInfoNotification, setPageSaving } = usePageLoader();

  const [openMenu, setOpenMenu] = useState(false);

  const [funcionarioCopyModal, setFuncionarioCopyModal] = useState(false);

  const [openReportsFuncionarios, setOpenReportsFuncionarios] = useState(false);
  const [openReportsTreinamentos, setOpenReportsTreinamentos] = useState(false);
  const [openReportsDocumentos, setOpenReportsDocumentos] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string>();
  const [funcionarios, setFuncionarios] = useState<FuncionarioTabela[]>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pageSize, setPageSize] = useState<number>();

  const [selectedFuncionario, setSelectedFuncionario] = useState<FuncionarioTabela>();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const [openLegendaModal, setOpenLegendaModal] = useState(false);

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>();
  const [alterarCentroCustoModal, setAlterarCentroCustoModal] = useState(false);

  const [reportViewerState, startReportViewerProcess] = useReportViewer();

  const navigate = useNavigate();

  const filters = useFuncionariosListaFilters();
  const pagination = useFuncionariosListaPagination();

  /**
   * Funções de carregamento
   */

  const loadFuncionarioSetoresDropDown = useCachedDataLoader({
    key: CommonConstants.cacheKey.setoresDropDown,
    loader: async () => await getSetoresDropDownUseCase.getFuncionarioSetoresDropDown(),
    onSet: (dt) => useFuncionariosListaFilters.setState({ funcionarioSetoresLista: dt }),
    onError: (e) => {
      throw e;
    },
  });

  const loadFuncionarioNomesDropDown = useCachedDataLoader({
    key: CommonConstants.cacheKey.nomesDropDown,
    loader: async () => await getFuncionarioNomesUseCase.getFuncionarioNomesDropDown(),
    onSet: (dt) => useFuncionariosListaFilters.setState({ funcionarioNomesLista: dt }),
    onError: (e) => {
      throw e;
    },
  });

  const loadFuncionarioFiliaisDropDown = useCachedDataLoader({
    key: CommonConstants.cacheKey.nomesDropDown,
    loader: async () => await getFuncionarioFiliaisUseCase.getFuncionarioFiliaisDropDown(),
    onSet: (dt) => useFuncionariosListaFilters.setState({ funcionarioFiliaisLista: dt }),
    onError: (e) => {
      throw e;
    },
  });

  const loadTreinamentosDropDown = useCachedDataLoader({
    key: CommonConstants.cacheKey.treinamentosDropDown,
    loader: async () => await getTreinamentosDropDownUseCase.getTreinamentosDropDown(),
    onSet: (dt) => useFuncionariosListaFilters.setState({ treinamentosDropdown: dt }),
    onError: (e) => {
      throw e;
    },
  });

  const loadPageSize = useCallback(async () => {
    try {
      const pageSize = await getPageSizeUseCase.getPageSize(CommonConstants.pageIds.funcionario);
      setPageSize(pageSize);
    } catch (e: unknown) {
      throw e;
    }
  }, [getPageSizeUseCase]);

  const loadFuncionarios = useCallback(
    async (funcState?: LoadFuncionarioState) => {
      try {
        const filteredTipoPesquisa = funcState?.tipoPesquisa ?? filters.tipoPesquisa;
        const filteredPesquisa = funcState?.pesquisa ?? filters.pesquisa;
        const filteredSetor = funcState?.setor ?? filters.setor;
        const filteredFilial = funcState?.filial ?? filters.filial;
        const filteredAdmissaoDe = funcState?.admissaoDe ?? filters.admissaoDe;
        const filteredAdmissaoAte = funcState?.admissaoAte ?? filters.admissaoAte;
        const filteredDemissaoDe = funcState?.demissaoDe ?? filters.demissaoDe;
        const filteredDemissaoAte = funcState?.demissaoAte ?? filters.demissaoAte;
        const filteredAfastamentoDe = funcState?.afastamentoDe ?? filters.afastamentoDe;
        const filteredAfastamentoAte = funcState?.afastamentoAte ?? filters.afastamentoAte;
        const filteredFeriasDe = funcState?.feriasDe ?? filters.feriasDe;
        const filteredFeriasAte = funcState?.feriasAte ?? filters.feriasAte;
        const filteredCentroDeCusto = funcState?.centroDeCusto ?? filters.centroDeCusto;
        const filteredStatus = funcState?.status ?? filters.status;
        const filteredTreinamentos = funcState?.treinamentos ?? filters.treinamentos;
        const filteredPrazoExperiencia = funcState?.prazoExperiencia ?? filters.prazoExperiencia;
        const filteredDocumentos = funcState?.documentos ?? filters.documentos;

        const sortField = funcState?.sortField ?? pagination.sortField;
        const sort = funcState?.sort ?? pagination.sort;
        const page = funcState?.page ?? pagination.page;
        const rowsPerPage = funcState?.pageSize ?? pagination.rowsPerPage;

        await getFuncionariosUseCase.getFuncionarios(
          TelasIds.Funcionarios_lista,
          {
            tipoPesquisa: filteredTipoPesquisa,
            pesquisa: filteredPesquisa,
            setor: filteredSetor,
            filial: filteredFilial,
            admissaoDe: filteredAdmissaoDe,
            admissaoAte: filteredAdmissaoAte,
            demissaoDe: filteredDemissaoDe,
            demissaoAte: filteredDemissaoAte,
            afastamentoDe: filteredAfastamentoDe,
            afastamentoAte: filteredAfastamentoAte,
            feriasDe: filteredFeriasDe,
            feriasAte: filteredFeriasAte,
            treinamentos: filteredTreinamentos,
            prazoExperiencia: filteredPrazoExperiencia,
            documentos: filteredDocumentos,
            centroDeCusto: filteredCentroDeCusto,
            status: filteredStatus,
          },
          sortField,
          sort,
          page,
          rowsPerPage,
        );

        setFuncionarios(getFuncionariosUseCase.items);

        pagination.setPageCount(getFuncionariosUseCase.totalPages);
        pagination.setRowsPerPage(getFuncionariosUseCase.pageSize);
        pagination.setTotalItems(getFuncionariosUseCase.totalItems);
      } catch (e: unknown) {
        setFuncionarios([]);
        throw e;
      }
    },
    [filters, pagination, getFuncionariosUseCase],
  );

  /**
   * useEffects
   */

  usePageStartBootstrap(async () => {
    try {
      setErrorMessage(undefined);

      await loadCentroCustoDropDown((list) => useFuncionariosListaFilters.setState({ centroDeCustoLista: list }));
      await loadCargosDropDown((list) => useFuncionariosListaFilters.setState({ cargoLista: list }));
      await loadTreinamentosDropDown();
      await loadFuncionarioSetoresDropDown();
      await loadFuncionarioNomesDropDown();
      await loadFuncionarioFiliaisDropDown();
      await loadPageSize();
      await loadFuncionarios();
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao carregar pagina!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReloadPage = useCallback(async () => {
    try {
      setLoading(true);

      await loadCentroCustoDropDown((list) => useFuncionariosListaFilters.setState({ centroDeCustoLista: list }));
      await loadCargosDropDown((list) => useFuncionariosListaFilters.setState({ cargoLista: list }));
      await loadTreinamentosDropDown();
      await loadFuncionarioSetoresDropDown();
      await loadFuncionarioNomesDropDown();
      await loadFuncionarioFiliaisDropDown();
      await loadPageSize();
      await loadFuncionarios();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionario!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadCentroCustoDropDown,
    loadCargosDropDown,
    loadTreinamentosDropDown,
    loadFuncionarioSetoresDropDown,
    loadFuncionarioNomesDropDown,
    loadFuncionarioFiliaisDropDown,
    loadPageSize,
    loadFuncionarios,
    pushErrorNotification,
  ]);

  const handleFuncionarioCopySubmit = useCallback(async (form: FuncionarioCopyForm) => {
    if (selectedFuncionario) {
      try {
        setSaving(true);
        setPageSaving(true);

        await copyFuncionarioUseCase.copyFuncionario(
          selectedFuncionario.idFuncionario,
          form.idCentroCusto,
          form.idCargo,
          form.dataAdmissao,
        );

        await loadFuncionarios();
        setFuncionarioCopyModal(false);
        pushInfoNotification("Sucesso", "Funcionário copiado com sucesso!");
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao copiar funcionário!"));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    selectedFuncionario,
    copyFuncionarioUseCase,
    loadFuncionarios,
    pushInfoNotification,
    pushErrorNotification,
    setPageSaving,
  ]);

  const handleCloseMenu = useCallback((event: any) => {
    if (menuRef.current && menuRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpenMenu(false);
  }, []);

  const handleToggleMenu = useCallback(() => {
    setOpenMenu((prev) => !prev);
  }, []);

  const handleListKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpenMenu(false);
    } else if (event.key === "Escape") {
      setOpenMenu(false);
    }
  }, []);

  const handleNewFuncionario = useCallback(
    (openNewTab: boolean) => {
      if (openNewTab) {
        window.open("/Funcionarios_cadastro", "_blank", "noopener,noreferrer");
      } else {
        navigate("/Funcionarios_cadastro");
      }
    },
    [navigate],
  );

  const handleTipoPesquisaChange = useDebouncedCallback(
    debouncedRef,
    (newValue: number) => {
      useFuncionariosListaFilters.getState().setTipoPesquisa(newValue);
      return async () => {
        try {
          setLoading(true);
          useFuncionariosListaPagination.getState().setPage(1);
          await loadFuncionarios({ tipoPesquisa: newValue, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionario!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [loadFuncionarios, pushErrorNotification],
  );

  const handlePesquisaChange = useDebouncedCallback(
    debouncedRef,
    (newValue: string) => {
      useFuncionariosListaFilters.getState().setPesquisa(newValue);
      return async () => {
        try {
          setLoading(true);
          useFuncionariosListaPagination.getState().setPage(1);
          await loadFuncionarios({ pesquisa: newValue, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionario!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [loadFuncionarios, pushErrorNotification],
  );

  const handleFilialChange = useDebouncedCallback(
    debouncedRef,
    (newValue: string) => {
      useFuncionariosListaFilters.getState().setFilial(newValue);
      return async () => {
        try {
          setLoading(true);
          useFuncionariosListaPagination.getState().setPage(1);
          await loadFuncionarios({ filial: newValue, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionario!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [loadFuncionarios, pushErrorNotification],
  );

  const handleSetorChange = useDebouncedCallback(
    debouncedRef,
    (newValue: string) => {
      useFuncionariosListaFilters.getState().setSetor(newValue);
      return async () => {
        try {
          setLoading(true);
          useFuncionariosListaPagination.getState().setPage(1);
          await loadFuncionarios({ setor: newValue, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionario!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [loadFuncionarios, pushErrorNotification],
  );

  const handleAdmissaoChange = useDebouncedCallback(
    debouncedRef,
    (de: string, ate: string) => {
      useFuncionariosListaFilters.getState().setAdmissaoDe(de);
      useFuncionariosListaFilters.getState().setAdmissaoAte(ate);
      return async () => {
        try {
          setLoading(true);
          useFuncionariosListaPagination.getState().setPage(1);
          await loadFuncionarios({ admissaoDe: de, admissaoAte: ate, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionario!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [loadFuncionarios, pushErrorNotification],
  );

  const handleDemissaoChange = useDebouncedCallback(
    debouncedRef,
    (de: string, ate: string) => {
      useFuncionariosListaFilters.getState().setDemissaoDe(de);
      useFuncionariosListaFilters.getState().setDemissaoAte(ate);
      return async () => {
        try {
          setLoading(true);
          useFuncionariosListaPagination.getState().setPage(1);
          await loadFuncionarios({ demissaoDe: de, demissaoAte: ate, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionario!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [loadFuncionarios, pushErrorNotification],
  );

  const handleAfastamentoChange = useDebouncedCallback(
    debouncedRef,
    (de: string, ate: string) => {
      useFuncionariosListaFilters.getState().setAfastamentoDe(de);
      useFuncionariosListaFilters.getState().setAfastamentoAte(ate);
      return async () => {
        try {
          setLoading(true);
          useFuncionariosListaPagination.getState().setPage(1);
          await loadFuncionarios({ afastamentoDe: de, afastamentoAte: ate, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionario!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [loadFuncionarios, pushErrorNotification],
  );

  const handleFeriasChange = useDebouncedCallback(
    debouncedRef,
    (de: string, ate: string) => {
      useFuncionariosListaFilters.getState().setFeriasDe(de);
      useFuncionariosListaFilters.getState().setFeriasAte(ate);
      return async () => {
        try {
          setLoading(true);
          useFuncionariosListaPagination.getState().setPage(1);
          await loadFuncionarios({ feriasDe: de, feriasAte: ate, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionario!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [loadFuncionarios, pushErrorNotification],
  );

  const handleCentroDeCustoChange = useDebouncedCallback(
    debouncedRef,
    (newValue: number[]) => {
      useFuncionariosListaFilters.getState().setCentroDeCusto(newValue);
      return async () => {
        try {
          setLoading(true);
          useFuncionariosListaPagination.getState().setPage(1);
          await loadFuncionarios({ centroDeCusto: newValue, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionario!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [loadFuncionarios, pushErrorNotification],
  );

  const handleStatusChange = useDebouncedCallback(
    debouncedRef,
    (newValue: number) => {
      useFuncionariosListaFilters.getState().setStatus(newValue);
      return async () => {
        try {
          setLoading(true);
          useFuncionariosListaPagination.getState().setPage(1);
          await loadFuncionarios({ status: newValue, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionario!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [loadFuncionarios, pushErrorNotification],
  );

  const handleTreinamentoChange = useCallback(async (treinamentos: number) => {
    try {
      setLoading(true);
      useFuncionariosListaFilters.setState({ treinamentos });
      useFuncionariosListaPagination.getState().setPage(1);
      await loadFuncionarios({ treinamentos, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionario!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadFuncionarios,
    pushErrorNotification,
  ]);

  const handlePrazoExperienciaChange = useCallback(async (prazoExperiencia: number) => {
    try {
      setLoading(true);
      useFuncionariosListaFilters.setState({ prazoExperiencia });
      useFuncionariosListaPagination.getState().setPage(1);
      await loadFuncionarios({ prazoExperiencia, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionario!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadFuncionarios,
    pushErrorNotification,
  ]);

  const handleDocumentosChange = useCallback(async (documentos: number) => {
    try {
      setLoading(true);
      useFuncionariosListaFilters.setState({ documentos });
      useFuncionariosListaPagination.getState().setPage(1);
      await loadFuncionarios({ documentos, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionario!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadFuncionarios,
    pushErrorNotification,
  ]);

  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);

      useFuncionariosListaFilters.getState().clear();
      useFuncionariosListaPagination.getState().setPage(1);

      await loadFuncionarios({
        tipoPesquisa: 0,
        pesquisa: "",
        setor: "",
        filial: "",
        admissaoDe: "",
        admissaoAte: "",
        demissaoDe: "",
        demissaoAte: "",
        afastamentoDe: "",
        afastamentoAte: "",
        feriasDe: "",
        feriasAte: "",
        centroDeCusto: [],
        status: 1,
        page: 1,
      });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar relatorio!"));
    } finally {
      setLoading(false);
    }
  }, [loadFuncionarios, pushErrorNotification]);

  const handleFuncionarioEdit = useCallback(
    (id: number | undefined, openNewTab: boolean) => {
      if (funcionarios && id) {
        const funcionario = funcionarios.find((x) => x.idFuncionario === id);
        if (funcionario) {
          if (openNewTab) {
            window.open(`/Funcionarios_cadastro?id=${id}`, "_blank", "noopener,noreferrer");
          } else {
            navigate(`/Funcionarios_cadastro?id=${id}`);
          }
        }
      }
    },
    [funcionarios, navigate],
  );

  const handleFuncionarioDelete = useCallback(
    (id: number | undefined) => {
      if (funcionarios && id) {
        const funcionario = funcionarios.find((x) => x.idFuncionario === id);
        if (funcionario) {
          setSelectedFuncionario(funcionario);
          setOpenDeleteDialog(true);
        }
      }
    },
    [funcionarios],
  );

  const handleFuncionarioExportar = useCallback(
    async (idFuncionario: number | undefined) => {
      try {
        setLoading(true);
        if (idFuncionario) {
          await startReportViewerProcess({
            fetchCallback: async (requiredMimeType) =>
              await getReportCadastroFuncionarioUseCase.getReportCadastroFuncionario(requiredMimeType, idFuncionario),
          });
        }
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao gerar relatorio!"));
      } finally {
        setLoading(false);
      }
    },
    [getReportCadastroFuncionarioUseCase, startReportViewerProcess, pushErrorNotification],
  );

  const handleFuncionarioCopyModalOpen = useCallback((idFuncionario: number | null, openState: boolean) => {
    if (openState) {
      if (idFuncionario && funcionarios) {
        setSelectedFuncionario(funcionarios.find((x) => x.idFuncionario === idFuncionario));
      } else {
        setSelectedFuncionario(undefined);
      }
    }
    setFuncionarioCopyModal(openState);
  }, [
    funcionarios,
  ]);

  const handleRowsPerPageChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        useFuncionariosListaPagination.getState().setPage(1);
        await loadFuncionarios({ page: 1, pageSize: rowsPerPage });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionario!"));
      } finally {
        setLoading(false);
      }
    },
    [loadFuncionarios, pushErrorNotification],
  );

  const handlePageChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        useFuncionariosListaPagination.getState().setPage(page + 1);
        await loadFuncionarios({ page: page + 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionario!"));
      } finally {
        setLoading(false);
      }
    },
    [loadFuncionarios, pushErrorNotification],
  );

  const handleCloseDeleteDialog = useCallback(() => {
    setOpenDeleteDialog(false);
  }, []);

  const handleDeleteFuncionario = useCallback(async () => {
    if (selectedFuncionario) {
      try {
        setSaving(true);

        await deleteFuncionarioUseCase.deleteFuncionario(TelasIds.Funcionarios_lista, selectedFuncionario.idFuncionario);

        if (funcionarios && funcionarios.length > 1) {
          await loadFuncionarios();
        } else {
          useFuncionariosListaPagination.getState().setPage(1);
          await loadFuncionarios({ page: 1 });
        }
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao deletar usuário!"));
      } finally {
        setSaving(false);
        setOpenDeleteDialog(false);
      }
    }
  }, [selectedFuncionario, funcionarios, deleteFuncionarioUseCase, loadFuncionarios, pushErrorNotification]);

  const handleReportsFuncionariosOpenState = useCallback((openState: boolean) => {
    setOpenReportsFuncionarios(openState);
    setOpenMenu(false);
  }, []);

  const handleReportsFuncionarios = useCallback(
    async (form: ReportsFuncionariosForm) => {
      try {
        setLoading(true);

        await startReportViewerProcess({
          fetchCallback: async (requiredMimeType) =>
            await getReportFuncionariosUseCase.getReportFuncionarios(
              requiredMimeType,
              idUsuario,
              form.setor,
              form.filial,
              form.status,
              form.nome,
              form.centroCusto,
              new RangedDate(form.dataAdmissao),
              new RangedDate(form.dataDemissao),
              new RangedDate(form.dataFerias),
            ),
          closeReportModalCallback: () => {
            setOpenReportsFuncionarios(false);
            setOpenMenu(false);
          },
        });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao gerar relatorio!"));
      } finally {
        setLoading(false);
      }
    },
    [idUsuario, getReportFuncionariosUseCase, startReportViewerProcess, pushErrorNotification],
  );

  const handleReportsTreinamentosOpenState = useCallback((openState: boolean) => {
    setOpenReportsTreinamentos(openState);
    setOpenMenu(false);
  }, []);

  const handleReportsTreinamentos = useCallback(
    async (form: ReportsTreinamentosForm) => {
      try {
        setLoading(true);
        await startReportViewerProcess({
          fetchCallback: async (requiredMimeType) =>
            await getReportTreinamentosUseCase.getReportTreinamentos(
              requiredMimeType,
              idUsuario,
              form.status,
              form.nome,
              form.centroCusto,
              form.eficaz,
              form.treinamentos,
              form.cargos,
              new RangedDate(form.dataTreinamento),
            ),
          closeReportModalCallback: () => {
            setOpenReportsTreinamentos(false);
            setOpenMenu(false);
          },
        });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao gerar relatorio!"));
      } finally {
        setLoading(false);
      }
    },
    [idUsuario, getReportTreinamentosUseCase, startReportViewerProcess, pushErrorNotification],
  );

  const handleReportsDocumentosOpenState = useCallback((openState: boolean) => {
    setOpenReportsDocumentos(openState);
    setOpenMenu(false);
  }, []);

  const handleReportsDocumentos = useCallback(
    async (form: ReportsDocumentosForm) => {
      try {
        setLoading(true);
        await startReportViewerProcess({
          fetchCallback: async (requiredMimeType) =>
            await getReportDocumentosUseCase.getReportDocumentos(
              requiredMimeType,
              idUsuario,
              form.centroCusto,
              form.funcionario,
              form.statusDocumento,
              form.situacao,
              form.statusFuncionario,
              new RangedDate(form.dataVencimento),
              new RangedDate(form.dataEmissao),
            ),
          closeReportModalCallback: () => {
            setOpenReportsDocumentos(false);
            setOpenMenu(false);
          },
        });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao gerar relatorio!"));
      } finally {
        setLoading(false);
      }
    },
    [idUsuario, getReportDocumentosUseCase, startReportViewerProcess, pushErrorNotification],
  );

  const handleRowSelectionModelChange = useCallback((rows: GridRowSelectionModel) => {
    setRowSelectionModel(rows);
  }, []);

  const handleOpenAlterarCentroCustoModal = useCallback(
    (openState: boolean) => {
      if (openState) {
        if (!rowSelectionModel || rowSelectionModel.ids.size === 0) {
          pushWarnNotification("Atenção", "Selecione os funcionarios a serem alterados!");
          return;
        }
      }
      setAlterarCentroCustoModal(openState);
    },
    [rowSelectionModel, pushWarnNotification],
  );

  const handleAlterarCentroCustoSubmit = useCallback(
    async (form: AlterarCentroCustoForm) => {
      try {
        setSaving(true);
        if (rowSelectionModel) {
          const idFuncionarios: number[] = [];
          for (let row of rowSelectionModel.ids.values()) {
            if (typeof row === "number") {
              idFuncionarios.push(row);
            } else if (typeof row === "string") {
              try {
                idFuncionarios.push(Number(row));
              } catch (e) { }
            }
          }

          await alterarCentroCustoFuncionariosUseCase.alterarCentroCustoFuncionarios(form.centroCusto, idFuncionarios);

          await loadFuncionarios();
        }
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao atualizar centro de custos!"));
      } finally {
        setSaving(false);
        setAlterarCentroCustoModal(false);
        setRowSelectionModel({ type: "include", ids: new Set() });
      }
    },
    [rowSelectionModel, alterarCentroCustoFuncionariosUseCase, pushErrorNotification, loadFuncionarios],
  );

  const handleShowLegenda = useCallback((openState: boolean) => {
    setOpenLegendaModal(openState);
  }, []);

  return {
    menuRef,
    openMenu,
    funcionarioCopyModal,
    openReportsFuncionarios,
    openReportsTreinamentos,
    openReportsDocumentos,
    loading,
    saving,
    funcionarios: funcionarios ?? [],
    pagination,
    pageSize,
    errorMessage,
    reportViewerState,
    filters,
    selectedFuncionario: selectedFuncionario ?? null,
    openDeleteDialog,
    openLegendaModal,
    rowSelectionModel,
    alterarCentroCustoModal,
    handleReloadPage,
    handleFuncionarioCopySubmit,
    handleToggleMenu,
    handleCloseMenu,
    handleListKeyDown,
    handleNewFuncionario,
    handleTipoPesquisaChange,
    handlePesquisaChange,
    handleFilialChange,
    handleSetorChange,
    handleAdmissaoChange,
    handleDemissaoChange,
    handleAfastamentoChange,
    handleFeriasChange,
    handleCentroDeCustoChange,
    handleStatusChange,
    handleTreinamentoChange,
    handlePrazoExperienciaChange,
    handleDocumentosChange,
    handleClearFilters,
    handleFuncionarioEdit,
    handleFuncionarioDelete,
    handleFuncionarioExportar,
    handleFuncionarioCopyModalOpen,
    handleRowsPerPageChange,
    handlePageChange,
    handleCloseDeleteDialog,
    handleDeleteFuncionario,
    handleReportsFuncionariosOpenState,
    handleReportsFuncionarios,
    handleReportsTreinamentosOpenState,
    handleReportsTreinamentos,
    handleReportsDocumentosOpenState,
    handleReportsDocumentos,
    handleRowSelectionModelChange,
    handleOpenAlterarCentroCustoModal,
    handleAlterarCentroCustoSubmit,
    handleShowLegenda,
  };
};
